import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { BaseLayout } from "@/app/layouts";
import { UserRole } from "@/shared/types/enums/UserRole.enum";
import { ROUTER_PATHS } from "@/shared/config/router-paths";
import { selectAuthParams } from "@/entities/auth/model/selectors";
import {
  selectActiveTab,
  selectCurrentUser,
} from "@/entities/common/model/selectors";

import { AuthRouter } from "./auth/AuthRouter";
import { BackOfficeRouter } from "./back-office/BackOfficeRouter";
import { ClientAreaRouter } from "./client-area/ClientAreaRouter";
import { ChunkLoadBoundary } from "./ui/ChunkLoadBoundary";

export function AppRouterProvider(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeScreen, authParams, currentUser } = {
    authParams: useSelector(selectAuthParams),
    currentUser: useSelector(selectCurrentUser),
    activeScreen: useSelector(selectActiveTab),
  };
  const isAuthBootstrapPending =
    !authParams.isChecked ||
    (authParams.isAuth && currentUser.role === UserRole.UNAUTHORIZED);

  const getDefaultPathByUserRole = (role?: UserRole): string => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
      case UserRole.ADMIN:
        return ROUTER_PATHS.backOffice.defaultPath;
      case UserRole.CLIENT_MANAGER:
      case UserRole.CLIENT_USER:
        return ROUTER_PATHS.clientArea.defaultPath;
      default:
        return ROUTER_PATHS.auth.defaultPath;
    }
    // ToDo add other Vendor Operator
    // return ROUTE_PATH.clientArea.specimenSearch.path;
  };

  const getRouterByUserRole = (role?: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN:
      case UserRole.ADMIN:
        return <Route path="/*" element={<BackOfficeRouter />} />;
      case UserRole.CLIENT_MANAGER:
      case UserRole.CLIENT_USER:
        return <Route path="/*" element={<ClientAreaRouter />} />;
      default:
        return <Route path="/*" element={<AuthRouter />} />;
    }
  };

  useEffect(() => {
    if (isAuthBootstrapPending) {
      return;
    }

    if (!authParams.isAuth) {
      navigate(ROUTER_PATHS.auth.defaultPath);
      return;
    }

    if (
      location.pathname === "/" ||
      location.pathname === ROUTER_PATHS.auth.defaultPath
    ) {
      if (activeScreen) {
        navigate(activeScreen.path);
        return;
      }

      navigate(getDefaultPathByUserRole(currentUser.role));
    }
  }, [
    authParams,
    navigate,
    activeScreen,
    currentUser,
    location.pathname,
    isAuthBootstrapPending,
  ]);

  return (
    <BaseLayout
      role={currentUser.role}
      isInitializing={isAuthBootstrapPending}
    >
      <ChunkLoadBoundary>
        <Suspense fallback={<div>Loading...</div>}>
          {!isAuthBootstrapPending ? (
            <Routes>
              <Route
                index
                element={<Navigate to={ROUTER_PATHS.defaultPath} replace />}
              />
              {getRouterByUserRole(currentUser.role)}
            </Routes>
          ) : null}
        </Suspense>
      </ChunkLoadBoundary>
    </BaseLayout>
  );
}
